<template>
    <v-dialog v-model="dialog" :max-width="maxWidth">
      <template v-slot:activator="{ on }">
        <v-btn color="primary" text class="header-list-button" v-on="on">
          {{ $t("pages.payouts.dialog.open") }}
        </v-btn>
      </template>
      <v-card v-if="dialog">
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headers"
            :items="charges"
            :server-items-length="totalCharges"
            :loading="chargesLoading"
            hide-default-footer
            :multi-sort="false"
            :must-sort="true"
            @page-count="pageCount = $event"
            :page.sync="page"
            :options.sync="options"
            :items-per-page="itemsPerPage"
            :footer-props="{ showFirstLastPage: true }"
            class="ic-table elevation-0 ic-table--heavy-headers"
          >
            <template v-slot:top>
              <v-container fluid class="white header-container">
                <v-row align="center" dense justify="center">
                  <v-col cols="12" md="6" class="v-toolbar__title">
                    {{ $t("pages.payouts.dialog.title", { amount: formattedAmount(payout.amount), date: formatDateTitle(payout.statusAt), totalCharges }) }}
                  </v-col>
                  <v-col cols="12" md="6" class="v-toolbar__title">
                    <div class="d-flex flex-wrap justify-end">
                      <v-btn
                        color="primary"
                        :loading="reportGenerating"
                        :disabled="disableDownload"
                        @click="downloadReport()"
                        class="toolbar-item header-list-button"
                      >
                        <span>{{ $t("pages.dispute.list.download") }}</span>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-container>

              <div class="header-separator"></div>
            </template>
            <template v-slot:header.booking.bookingChannelName="{ header }">
              <span class="custom-table-header">{{ header.text }}</span>
            </template>
            <template v-slot:header.captureDate="{ header }">
              <span class="custom-table-header">{{ header.text }}</span>
            </template>
            <template v-slot:header.booking.dispatchId="{ header }">
              <span class="custom-table-header">{{ header.text }}</span>
            </template>
            <template v-slot:header.card.brand="{ header }">
              <span class="custom-table-header">{{ header.text }}</span>
            </template>
            <template v-slot:header.netAmount="{ header }">
              <span class="custom-table-header">{{ header.text }}</span>
            </template>

            <template v-slot:header.booking.amount="{ header }">
              <span class="custom-table-header">{{ header.text }}</span>
            </template>

            <template v-slot:item.captureDate="{ item }">
              <span>{{ formatDateTime(item.captureDate) }}</span>
            </template>

            <template v-slot:item.netAmount="{ item }">
              <span>{{ formattedAmount(item.netAmount) }}</span>
            </template>

            <template v-slot:item.applicationFee="{ item }">
              <span>{{ formattedAmount(item.applicationFee) }}</span>
            </template>

            <template v-slot:item.booking.amount="{ item }">
              <span>{{ formattedAmount(item.booking.amount) }}</span>
            </template>

            <template v-slot:item.card.brand="{ item }">
              <span>
                <v-icon>{{ creditCardIcon(item.card.brand) }}</v-icon>
              </span>
            </template>

            <template v-slot:item.netAmount="{ item }">
              <template v-if="item.payoutAmount !== null">
                {{ item.payoutAmount }}
              </template>
              <template v-else>
                {{ item.netAmount }}
              </template>
            </template>
          </v-data-table>
          <div class="text-center pt-8">
            <v-pagination v-model="page" :length="pageCount" total-visible="10"></v-pagination>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog = false">{{
            $t("pages.payouts.dialog.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>
<script>
import { isMobile } from '../mixins/responsive.mixins';
import { creditCardIcon } from '../mixins/creditCardIcons.mixins';
import dayjs from '../plugins/dayjs';

export default {
  props: {
    chargesLoading: {
      type: Boolean,
      required: true,
    },
    charges: {
      type: Array,
      required: true,
    },
    totalCharges: {
      type: Number,
      required: true,
    },
    payout: {
      type: Object,
      required: true,
    },
    currencySymbol: {
      type: String,
      required: true,
    },
    reportGenerating: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    dialog: false,
    page: 1,
    pageCount: 0,
    itemsPerPage: 100,
    options: {
      sortBy: ['createdAt'], sortDesc: [true],
    },
  }),
  mixins: [
    isMobile,
    creditCardIcon,
  ],
  watch: {
    options: {
      handler() {
        this.getCharges();
      },
      deep: true,
    },
  },
  computed: {
    maxWidth() {
      if (this.isMobile) {
        return '100%';
      }
      return '1200px';
    },
    headers() {
      return [
        {
          text: this.$t('pages.payouts.dialog.bookingChannelName'),
          align: 'left',
          sortable: false,
          value: 'booking.bookingChannelName',
        },
        {
          text: this.$t('pages.payouts.dialog.captureDate'),
          align: 'left',
          sortable: true,
          value: 'captureDate',
        },
        {
          text: this.$t('pages.payouts.dialog.bookingId'),
          align: 'left',
          sortable: true,
          value: 'booking.dispatchId',
        },
        {
          text: this.$t('pages.payouts.dialog.cardBrand'),
          align: 'left',
          sortable: true,
          value: 'card.brand',
        },
        {
          text: this.$t('pages.payouts.dialog.fareAmount'),
          align: 'left',
          sortable: true,
          value: 'booking.amount',
        },
        {
          text: this.$t('pages.payouts.dialog.netAmount'),
          align: 'left',
          sortable: true,
          value: 'netAmount',
        },
      ];
    },
    disableDownload() {
      return false;
    },
  },

  methods: {
    getCharges() {
      const { page, sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'createdAt';
      this.$emit('getChargesForPayout', {
        payoutId: this.payout.id,
        sort: sortDesc[0] ? `-${sort}` : sort,
        page: page - 1,
        maxPerPage: this.itemsPerPage,
      });
    },
    formatDate(date) {
      if (!date) return null;
      return dayjs(date).format('YYYY-MM-DD');
    },

    formatTime(date) {
      if (!date) return null;
      return dayjs(date).format('HH:mm:ss');
    },

    formatDateTime(date) {
      if (!date) return null;
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    },

    formatDateTitle(date) {
      if (!date) return '';
      return dayjs(date).format('MMM D, YYYY');
    },

    formattedAmount(amount) {
      return amount ? `${this.currencySymbol}${Number(amount).toFixed(2)}` : '';
    },

    downloadReport() {
      const { sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'createdAt';
      this.$emit('downloadReport', {
        sort: sortDesc[0] ? `-${sort}` : sort,
        payoutId: this.payout.id,
      });
    },
  },
};
</script>
<style scoped lang="scss">
.custom-table-header {
  font-size: 0.73rem !important;
}
</style>
